import { Button, Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle } from 'lucide-react';
import type { PmeLifeRow } from '../../proposal.mock';

type Props = {
  /** Todas as vidas da proposta — usadas para montar a lista do diálogo. */
  lives: PmeLifeRow[];
  livesWithoutCpf: PmeLifeRow[];
  livesWithoutName: PmeLifeRow[];
  onDismiss: () => void;
};

/**
 * Vidas sem nome ou sem CPF barram a assinatura: o documento assinado sairia
 * com vida incompleta. O diálogo lista exatamente quais corrigir.
 *
 * Aberto pelo `usePmeIncompleteLivesGuard` no Confirmar do Resumo e no
 * "Assinatura Digital" da etapa de Assinatura.
 */
export function PmeIncompleteLivesDialog({
  lives,
  livesWithoutCpf,
  livesWithoutName,
  onDismiss,
}: Props) {
  return (
    <Dialog.Root
      open={livesWithoutCpf.length > 0 || livesWithoutName.length > 0}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) onDismiss();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='w-[calc(100vw-1.5rem)] max-w-2xl max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-3xl bg-alba-white-500 p-0 shadow-xl'>
            <Dialog.Header className='border-b border-alba-gray-200 px-5 py-5 sm:px-6'>
              <Dialog.Title>
                <div className='flex items-start gap-3 pr-8'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-alba-yellow-100'>
                    <AlertTriangle className='h-5 w-5 text-alba-yellow-600' />
                  </div>
                  <div className='flex flex-col gap-1'>
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Vidas com dados pendentes
                    </Text>
                    <Text className='text-sm text-alba-gray-600'>
                      Nome e CPF sao obrigatorios para confirmar a proposta.
                    </Text>
                  </div>
                </div>
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Dialog.CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body className='max-h-[calc(100vh-14rem)] overflow-y-auto px-5 py-5 sm:px-6'>
              <Text className='text-sm text-alba-gray-700'>
                Atualize os dados das vidas abaixo antes de enviar a proposta
                para assinatura.
              </Text>
              <div className='mt-4 rounded-2xl border border-alba-gray-200 bg-alba-gray-100/40 p-4'>
                <Text className='text-sm font-semibold text-alba-black-500'>
                  {
                    new Set([
                      ...livesWithoutCpf.map(life => life.memberId),
                      ...livesWithoutName.map(life => life.memberId),
                    ]).size
                  }{' '}
                  vida(s) pendente(s)
                </Text>
                <ul className='mt-3 space-y-2'>
                  {lives
                    .filter(
                      life =>
                        livesWithoutCpf.some(
                          pendingLife => pendingLife.memberId === life.memberId
                        ) ||
                        livesWithoutName.some(
                          pendingLife => pendingLife.memberId === life.memberId
                        )
                    )
                    .map(life => {
                      const missingFields = [
                        !life.name?.trim() && 'nome',
                        !life.cpf?.trim() && 'CPF',
                      ]
                        .filter(Boolean)
                        .join(' e ');

                      return (
                        <li
                          key={life.memberId}
                          className='rounded-xl border border-alba-gray-200 bg-alba-white-500 px-3 py-2'
                        >
                          <Text className='text-sm text-alba-black-500'>
                            {life.name || 'Vida sem nome'}: {missingFields}{' '}
                            obrigatorio
                            {missingFields === 'nome e CPF' ? 's' : ''}.
                          </Text>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </Dialog.Body>
            <Dialog.Footer className='border-t border-alba-gray-200 px-5 py-4 sm:px-6'>
              <Button variant='primary' size='md' onClick={onDismiss}>
                Entendi
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
